import React, { createContext, useContext, type ComponentProps, type ReactNode } from 'react';
import { PrimaryButton } from '../../..';
import { cn } from '../../../utils/classnames';
import type { ToolUIPart } from 'ai';

type ToolUIPartApproval =
  | {
      id: string;
      approved?: never;
      reason?: never;
    }
  | {
      id: string;
      approved: boolean;
      reason?: string;
    }
  | {
      id: string;
      approved: true;
      reason?: string;
    }
  | {
      id: string;
      approved: false;
      reason?: string;
    }
  | undefined;

type ConfirmationContextValue = {
  approval: ToolUIPartApproval;
  state: ToolUIPart['state'] | string;
};

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null);

const useConfirmation = () => {
  const context = useContext(ConfirmationContext);

  if (!context) {
    throw new Error('Confirmation components must be used within Confirmation');
  }

  return context;
};

export type ConfirmationProps = ComponentProps<'div'> & {
  approval?: ToolUIPartApproval;
  state: ToolUIPart['state'] | string;
};

export const Confirmation = ({
  className,
  approval,
  state,
  children,
  ...props
}: ConfirmationProps) => {
  if (!approval || state === 'input-streaming' || state === 'input-available') {
    return null;
  }

  return (
    <ConfirmationContext.Provider value={{ approval, state }}>
      <div className={cn('core-confirmation', className)} {...props}>
        {children}
      </div>
    </ConfirmationContext.Provider>
  );
};

export type ConfirmationTitleProps = ComponentProps<'div'>;

export const ConfirmationTitle = ({
  className,
  ...props
}: ConfirmationTitleProps) => (
  <div className={cn('core-confirmation-title', className)} {...props} />
);

export type ConfirmationRequestProps = {
  children?: ReactNode;
};

export const ConfirmationRequest = ({ children }: ConfirmationRequestProps) => {
  const { state } = useConfirmation();

  if (state !== 'approval-requested') {
    return null;
  }

  return <>{children}</>;
};

export type ConfirmationAcceptedProps = {
  children?: ReactNode;
};

export const ConfirmationAccepted = ({
  children,
}: ConfirmationAcceptedProps) => {
  const { approval, state } = useConfirmation();

  if (
    !approval?.approved ||
    (state !== 'approval-responded' &&
      state !== 'output-denied' &&
      state !== 'output-available')
  ) {
    return null;
  }

  return <>{children}</>;
};

export type ConfirmationRejectedProps = {
  children?: ReactNode;
};

export const ConfirmationRejected = ({
  children,
}: ConfirmationRejectedProps) => {
  const { approval, state } = useConfirmation();

  if (
    approval?.approved !== false ||
    (state !== 'approval-responded' &&
      state !== 'output-denied' &&
      state !== 'output-available')
  ) {
    return null;
  }

  return <>{children}</>;
};

export type ConfirmationActionsProps = ComponentProps<'div'>;

export const ConfirmationActions = ({
  className,
  ...props
}: ConfirmationActionsProps) => {
  const { state } = useConfirmation();

  if (state !== 'approval-requested') {
    return null;
  }

  return (
    <div
      className={cn('core-confirmation-actions', className)}
      {...props}
    />
  );
};

export type ConfirmationActionProps = ComponentProps<typeof PrimaryButton>;

export const ConfirmationAction = ({ className, ...props }: ConfirmationActionProps) => (
  <PrimaryButton
    className={cn('core-confirmation-action', className)}
    size="small"
    {...props}
  />
);

Confirmation.displayName = 'Confirmation';
ConfirmationTitle.displayName = 'ConfirmationTitle';
ConfirmationRequest.displayName = 'ConfirmationRequest';
ConfirmationAccepted.displayName = 'ConfirmationAccepted';
ConfirmationRejected.displayName = 'ConfirmationRejected';
ConfirmationActions.displayName = 'ConfirmationActions';
ConfirmationAction.displayName = 'ConfirmationAction';

